import {
  Box,
  Checkbox,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  backend,
  canApplyDate,
  COVERSHEET_PROPS,
  displayDateAndTimeCompact,
  displayDateString,
  extractCaseIdAndName,
  isNumber,
  parseDate,
  parseSafeInt,
} from "Backend";
import { AppRoutes } from "AppRoutes";
import DocumentName from "components/DocumentName";
import SimpleMenu from "components/SimpleMenu";
import formatSizeBytes from "helpers/formatSizeBytes";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import theme, { EllipsisSX } from "theme";
import { P } from "theme/typography";
import { selectClientLens, selectDocumentProperties, selectVisibleDocumentProperties } from "redux/slices/clientLens";
import { useAppSelector } from "redux/hooks";
import { ActionItem } from "../case/intakes/IntakeRow";
import {
  CustomDocumentProperty,
  CustomPropertyType,
  DocumentIntakeViewBasic,
  FieldPresence,
  GetLensDocumentsDocument,
} from "generated/graphql";
import { selectEnableFirmFeatureSet, selectIsSupport } from "redux/slices/session";
import { Create, Delete, Edit, GetApp, InsertDriveFile, Refresh } from "@mui/icons-material";
import BulkDeleteIntakeDialog from "../case/intakes/BulkDeleteIntakeDialog";
import DeleteIntakeDialog from "../case/intakes/DeleteIntakeDialog";
import ReprocessIntakeDialog from "../case/intakes/ReprocessIntakeDialog";
import RenameIntakeDialog from "../case/intakes/RenameIntakeDialog";
import EditDocumentPropertiesDialog from "../case/intakes/EditDocumentPropertiesDialog";
import DebouncedText from "components/DebouncedText";
import CustomPropertyCell from "components/CustomPropertyCell";
import CaseFilter from "./CaseFilter";
import DateRangeFilter from "./DateRangeFilter";
import { fullNameFromParts } from "helpers/name-helpers";
import useSearchParamsWithUpdate from "helpers/hooks/useSearchParamsWithUpdate";
import ClearFiltersButton from "./ClearFiltersButton";
import doBulkDownload from "helpers/doBulkDownload";
import InfoIcon from "@mui/icons-material/Info";
import { runGqlQuery } from "gqlClient";
import SkeletonRow from "components/SkeletonRow";
import CoversheetsMultiCaseDialog from "../case/intakes/CoversheetsDialog";
import { add } from "date-fns";
import CheckAll from "./CheckAll";
import UserAutoComplete from "components/UserAutocomplete";
import { selectUsers } from "redux/slices/client";
import FirmAutocomplete from "components/FirmAutocomplete";
import {
  CustomPropertyValue,
  deserializeCustomPropertyValue,
  serializeCustomPropertyValue,
} from "helpers/customProperties";
import { useSnackbar } from "notistack";
import { useFirmsByLensId } from "helpers/hooks/useAllowedLenses";
import { useParamNumbers } from "helpers/hooks/useParamNumbers";

function compareDocumentIntakeViewBasic(a: DocumentIntakeViewBasic, b: DocumentIntakeViewBasic): boolean {
  return a.id === b.id;
}

const CustomPropertyFilter = ({
  customProperty,
  userSelect,
}: {
  customProperty: CustomDocumentProperty;
  userSelect?: boolean;
}) => {
  const { searchParams, updateParams } = useSearchParamsWithUpdate();
  const presences = searchParams.getAll("presences");
  const customPropertyValue = searchParams.get(customProperty.key) || "";
  const users = useAppSelector(selectUsers);

  const setValue = (value: CustomPropertyValue) => {
    if (customProperty.dataType === CustomPropertyType.Date) {
      if (!canApplyDate(customProperty.key, value as Date | undefined, searchParams)) return;
    }
    const presencesSet = new Set(searchParams.getAll("presences"));
    if (value) {
      presencesSet.add(customProperty.key);
    }
    updateParams([
      { key: "presences", value: Array.from(presencesSet) },
      { key: customProperty.key, value: serializeCustomPropertyValue(value) || "" },
    ]);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }} key={customProperty.key}>
      <Checkbox
        checked={!!presences.find((p) => p === customProperty.key)}
        onChange={(_evt, checked) => {
          const paramPresences = presences.slice();
          const propertyParam = searchParams.get(customProperty.key);

          if (checked) {
            paramPresences.push(customProperty.key);
          } else {
            const foundIndex = presences.findIndex((p) => p === customProperty.key);

            if (foundIndex > -1) {
              paramPresences.splice(foundIndex, 1);
            }
          }
          updateParams([
            { key: "presences", value: Array.from(new Set(paramPresences || presences)) },
            { key: customProperty.key, value: checked && propertyParam ? propertyParam : "" },
          ]);
        }}
      />
      {!userSelect ? (
        <CustomPropertyCell
          formControlProps={{
            fullWidth: true,
          }}
          textProps={{
            sx: { width: "100%" },
          }}
          numberProps={{
            sx: { width: "100%" },
          }}
          customProperty={customProperty}
          value={deserializeCustomPropertyValue(customPropertyValue, customProperty)}
          setValue={setValue}
        />
      ) : (
        <UserAutoComplete
          label="Approved By"
          options={users}
          selected={users.find((u) => fullNameFromParts(u.firstName, u.lastName) === customPropertyValue)}
          onChange={(user) => setValue(user ? fullNameFromParts(user.firstName, user.lastName) : undefined)}
        />
      )}
    </Box>
  );
};

const CoversheetFilters = ({ documentProperties }: { documentProperties: CustomDocumentProperty[] }) => {
  const { searchParams, updateParam } = useSearchParamsWithUpdate();
  const unapproved = searchParams.get("unapproved") === "true" ? true : false;
  const coversheetDateCustomProperty = documentProperties.find((dp) => dp.key === COVERSHEET_PROPS.DATE);
  const coversheetApprovedByCustomProperty = documentProperties.find((dp) => dp.key === COVERSHEET_PROPS.APPROVED_BY);
  const coversheetApprovedDateCustomProperty = documentProperties.find(
    (dp) => dp.key === COVERSHEET_PROPS.APPROVED_DATE
  );

  return (
    <>
      <P variant="subtitle3">Coversheets</P>
      <FormControlLabel
        sx={{ ml: 0, color: "grey.400" }}
        control={
          <Checkbox
            checked={unapproved}
            onChange={(_evt, checked) => updateParam("unapproved", !checked ? null : "true")}
          />
        }
        label="Unapproved"
      />
      {coversheetDateCustomProperty && <CustomPropertyFilter customProperty={coversheetDateCustomProperty} />}
      {coversheetApprovedByCustomProperty && (
        <CustomPropertyFilter customProperty={coversheetApprovedByCustomProperty} userSelect />
      )}
      {coversheetApprovedDateCustomProperty && (
        <CustomPropertyFilter customProperty={coversheetApprovedDateCustomProperty} />
      )}
    </>
  );
};

interface LensDocumentIntakeRowProps {
  intake: DocumentIntakeViewBasic;
  selected: boolean;
  onSelect: (intake: DocumentIntakeViewBasic) => void;
  actionItems: ActionItem[];
  showCoversheets: boolean;
  documentProperties: CustomDocumentProperty[];
}

const LensDocumentIntakeRow = ({
  intake,
  selected,
  onSelect,
  actionItems,
  showCoversheets,
  documentProperties,
}: LensDocumentIntakeRowProps) => {
  const lens = useAppSelector(selectClientLens);
  const iAmSupport = useAppSelector(selectIsSupport);
  const hasFirmFeatureSet = useAppSelector(selectEnableFirmFeatureSet);

  const visibleProperties = documentProperties.filter((dp) => intake.properties.find((p) => p.key === dp.key)?.value);

  const firms = useFirmsByLensId(lens.id);
  const firmName = useMemo(() => {
    const firm = firms.find((f) => f.id === intake.firmId);
    return firm ? firm.name : "";
  }, [firms, intake.firmId]);

  return (
    <TableRow key={intake.name}>
      <TableCell sx={{ px: 0 }}>
        <Checkbox
          checked={selected}
          onChange={() => {
            onSelect(intake);
          }}
          size="small"
          color="secondary"
        />
      </TableCell>
      <TableCell align="left">
        <SimpleMenu hamburger items={actionItems} />
      </TableCell>
      <TableCell>{fullNameFromParts(intake.claimantGivenName, intake.claimantSurname)}</TableCell>
      <TableCell>{intake.clientCaseId}</TableCell>
      <TableCell sx={EllipsisSX}>
        <Link
          to={AppRoutes.caseDocumentsBuilder({
            lensId: lens.id,
            caseId: intake.caseId,
            documentId: intake.id,
          })}>
          <DocumentName name={intake.name} className="max-w-96" />
        </Link>
      </TableCell>
      {iAmSupport && <TableCell>{intake.id}</TableCell>}
      <Tooltip
        title={displayDateAndTimeCompact(intake.createdAt)}
        arrow
        PopperProps={{
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -24],
              },
            },
          ],
        }}>
        <TableCell>{displayDateString(intake.createdAt)}</TableCell>
      </Tooltip>
      <TableCell>{formatSizeBytes(intake.sizeBytes)}</TableCell>
      <TableCell>{intake.pages}</TableCell>
      {showCoversheets && (
        <>
          <TableCell>
            {displayDateString(intake.properties.find((p) => p.key === COVERSHEET_PROPS.DATE)?.value)}
          </TableCell>
          <TableCell>
            {displayDateString(intake.properties.find((p) => p.key === COVERSHEET_PROPS.APPROVED_DATE)?.value)}
          </TableCell>
          <TableCell>{intake.properties.find((p) => p.key === COVERSHEET_PROPS.APPROVED_BY)?.value}</TableCell>
        </>
      )}

      {documentProperties.length > 0 ? (
        <TableCell>
          <div>
            {visibleProperties.map((dp) => (
              <Box key={dp.key} sx={{ display: "flex", alignItems: "center" }} style={{ whiteSpace: "nowrap" }}>
                <span>
                  <strong>{dp.displayName}:</strong> {intake.properties.find((p) => p.key === dp.key)?.value}
                </span>
                {dp.preventsLensHits && (
                  <Tooltip arrow title="Prevents lens hits">
                    <InfoIcon sx={{ ml: 1 }} fontSize="small" color="secondary" />
                  </Tooltip>
                )}
              </Box>
            ))}
          </div>
        </TableCell>
      ) : (
        <></>
      )}
      {hasFirmFeatureSet && <TableCell>{firmName}</TableCell>}
    </TableRow>
  );
};

const LensDocuments = () => {
  const { searchParams, updateParam, updateParams } = useSearchParamsWithUpdate();
  const name = searchParams.get("name");
  const iAmSupport = useAppSelector(selectIsSupport);
  const { lensId } = useParamNumbers(["lensId"]);
  const [documents, setDocuments] = useState<DocumentIntakeViewBasic[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [inc, setInc] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const intakesPagination = {
    total,
    page: 1,
  };
  const incrementCounter = () => {
    setInc((prev) => prev + 1);
  };
  const searchParamsPage = searchParams.get("page");
  const qpFirmId = searchParams.get("firmId");
  const firmId = parseSafeInt(qpFirmId);
  const searchParamsRowsPerPage = searchParams.get("rowsPerPage");
  const pagination = {
    page: searchParamsPage !== null ? parseInt(searchParamsPage) : 1,
    rowsPerPage: searchParamsRowsPerPage !== null ? parseInt(searchParamsRowsPerPage) : 50,
  };
  const [selectedIntakes, setSelectedIntakes] = useState<DocumentIntakeViewBasic[]>([]);
  const documentProperties = useAppSelector(selectDocumentProperties);
  const visibleDocumentProperties = useAppSelector(selectVisibleDocumentProperties);
  const hasFirmFeatureSet = useAppSelector(selectEnableFirmFeatureSet);
  const showCoversheets = !!documentProperties.find((dp) => dp.key === COVERSHEET_PROPS.DATE);
  const [selectedIntake, setSelectedIntake] = useState<DocumentIntakeViewBasic | undefined>(undefined);
  const [deleteIntakeOpen, setDeleteIntakeOpen] = useState(false);
  const [bulkDeleteIntakeOpen, setBulkDeleteIntakeOpen] = useState(false);
  const closeDeleteIntake = () => {
    setSelectedIntake(undefined);
    setDeleteIntakeOpen(false);
    incrementCounter();
  };
  const [renameIntakeOpen, setRenameIntakeOpen] = useState(false);
  const [reprocessIntakeOpen, setReprocessIntakeOpen] = useState(false);
  const closeReprocessIntake = () => {
    setSelectedIntake(undefined);
    setReprocessIntakeOpen(false);
  };
  const [editDocumentProperties, setEditDocumentProperties] = useState(false);
  const closeEditDocumentProperties = () => {
    setSelectedIntake(undefined);
    setEditDocumentProperties(false);
    incrementCounter();
  };
  const [createCoversheets, setCreateCoversheets] = useState(false);
  const intakeWithCoversheet = useMemo(
    () => selectedIntakes.find((i) => i.properties.find((p) => p.key === COVERSHEET_PROPS.DATE)?.value),
    [selectedIntakes]
  );
  const coversheetInitialDate = intakeWithCoversheet
    ? intakeWithCoversheet.properties.find((p) => p.key === COVERSHEET_PROPS.DATE)?.value
    : undefined;
  const numColumns = 9 + (iAmSupport ? 1 : 0) + (showCoversheets ? 3 : 0);

  useEffect(() => {
    if (lensId) {
      setLoading(true);
      const cases = searchParams.getAll("cases");
      const textQuery = searchParams.get("name");
      const presences = searchParams.getAll("presences");
      const qpPage = searchParams.get("page");
      const qpLimit = searchParams.get("rowsPerPage");
      const limit = qpLimit !== null ? parseInt(qpLimit) : 50;
      const page = qpPage !== null ? parseInt(qpPage) - 1 : 0;
      const uploadDateStart = searchParams.get("uploadDateStart");
      const uploadDateEnd = searchParams.get("uploadDateEnd");
      const unapproved = searchParams.get("unapproved") === "true" ? true : false;
      const minCreatedAt = uploadDateStart !== null ? new Date(uploadDateStart).toISOString() : undefined;
      const maxCreatedAt = uploadDateEnd !== null ? add(new Date(uploadDateEnd), { days: 1 }).toISOString() : undefined;
      // find documents where the property is not present
      const missings = unapproved ? [COVERSHEET_PROPS.APPROVED_DATE] : [];

      runGqlQuery(GetLensDocumentsDocument, {
        lensId,
        textQuery,
        offset: page * limit,
        firmId,
        limit,
        minCreatedAt,
        maxCreatedAt,
        documentPropertyQueries: presences
          .map((p) => ({
            key: p,
            value: searchParams.get(p) !== null ? searchParams.get(p) : null,
            exact: documentProperties.find((dp) => dp.key === p)?.dataType === CustomPropertyType.Text ? false : true,
            ...(!searchParams.get(p) ? { presence: FieldPresence.NonNull } : {}),
          }))
          .concat(
            missings.map((m) => ({
              key: m,
              presence: FieldPresence.Null,
              value: null,
              exact: false,
            }))
          ),
        caseIds: cases.map((c) => extractCaseIdAndName(c)[0]).filter(isNumber),
      })
        .then((r) => {
          setDocuments(r.lens.queryDocuments.documents);
          setTotal(r.lens.queryDocuments.total);
          setLoading(false);
        })
        .catch(() => {
          enqueueSnackbar("There was a problem loading the documents. Please try again later.", {
            variant: "error",
          });
          setLoading(false);
        });
    }
  }, [searchParams, lensId, inc]);

  /**
   * Intake Actions
   */
  const actionItems = (intake: DocumentIntakeViewBasic) => {
    const ret: ActionItem[] = [];

    if (iAmSupport) {
      ret.push({
        name: "Reprocess",
        icon: <Refresh color="action" />,
        action: () => {
          setSelectedIntake(intake);
          setReprocessIntakeOpen(true);
        },
      });
    }
    ret.push(
      {
        name: "Edit Properties",
        icon: <Edit color="action" />,
        action: () => {
          setSelectedIntake(intake);
          setEditDocumentProperties(true);
        },
      },
      {
        name: "Rename",
        icon: <Create color="action" />,
        action: () => {
          setSelectedIntake(intake);
          setRenameIntakeOpen(true);
        },
      }
    );

    return ret;
  };

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[100]}`,
          p: 2,
          borderRadius: `${theme.shape.borderRadius}px`,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          overflow: "auto",
        }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <P variant="subtitle1heavy">Filters</P>
          <ClearFiltersButton qpIgnore={["page", "rowsPerPage", "inc"]} />
        </Box>
        {hasFirmFeatureSet && (
          <FirmAutocomplete
            lensId={lensId}
            firmId={firmId}
            onChange={(fId) => updateParam("firmId", fId ? `${fId}` : undefined)}
          />
        )}
        <CaseFilter />
        <DebouncedText
          value={name === null ? "" : name}
          label="Document Name"
          onChange={(n) => updateParam("name", n)}
        />
        <DateRangeFilter
          startKey="uploadDateStart"
          startLabel="Upload Date Start"
          endKey="uploadDateEnd"
          endLabel="Upload Date End"
        />
        {showCoversheets ? <CoversheetFilters documentProperties={documentProperties} /> : <></>}
        {visibleDocumentProperties.length > 0 ? (
          <>
            <P variant="subtitle3">Properties</P>
            {visibleDocumentProperties.map((dp) => (
              <CustomPropertyFilter customProperty={dp} key={dp.key} />
            ))}
          </>
        ) : (
          <></>
        )}
      </Box>
      <Box
        sx={{
          padding: 0.5,
          backgroundColor: "white",
          border: `1px solid ${theme.palette.grey[100]}`,
          borderTopLeftRadius: `${theme.shape.borderRadius}px`,
          borderTopRightRadius: `${theme.shape.borderRadius}px`,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "auto",
        }}>
        <Box sx={{ display: "flex", ml: 1, my: 1 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${theme.palette.grey[100]}`,
              borderRadius: "8px",
              px: 2,
            }}>
            <P>Bulk Actions</P>
            <Tooltip title="Delete" arrow>
              <span>
                <IconButton
                  disabled={!selectedIntakes.length}
                  id="document_table_delete"
                  onClick={() => {
                    if (selectedIntakes.length) {
                      setBulkDeleteIntakeOpen(true);
                    }
                  }}>
                  <Delete />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Download" arrow>
              <span>
                <IconButton
                  disabled={!selectedIntakes.length}
                  id="document_table_download"
                  onClick={() =>
                    doBulkDownload(selectedIntakes.map((intake) => backend.getIntakeDownloadUrl(intake.id)))
                  }>
                  <GetApp />
                </IconButton>
              </span>
            </Tooltip>
            {showCoversheets && (
              <Tooltip title="Approve Coversheets" arrow>
                <span>
                  <IconButton disabled={!selectedIntakes.length} onClick={() => setCreateCoversheets(true)}>
                    <InsertDriveFile />
                  </IconButton>
                </span>
              </Tooltip>
            )}
            {selectedIntakes.length > 0 && (
              <P variant="xs">
                {selectedIntakes.length} {selectedIntakes.length === 1 ? "Document" : "Documents"} Selected
              </P>
            )}
            {selectedIntakes.length > 0 && createCoversheets && (
              <CoversheetsMultiCaseDialog
                open={createCoversheets}
                close={() => {
                  setCreateCoversheets(false);
                  incrementCounter();
                  setSelectedIntakes([]);
                }}
                intakes={selectedIntakes}
                coversheetInitialDate={
                  coversheetInitialDate !== undefined ? parseDate(coversheetInitialDate) : undefined
                }
              />
            )}
          </Box>
          <Box sx={{ flexGrow: 1 }} />
        </Box>
        <TableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  th: {
                    whiteSpace: "nowrap",
                  },
                }}>
                <TableCell sx={{ px: 0 }}>
                  <CheckAll<DocumentIntakeViewBasic>
                    selectedItems={selectedIntakes}
                    items={documents}
                    setItems={setSelectedIntakes}
                    compareFn={compareDocumentIntakeViewBasic}
                  />
                </TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Claimant</TableCell>
                <TableCell>Case Id</TableCell>
                <TableCell>Name</TableCell>
                {iAmSupport && <TableCell>Id</TableCell>}
                <TableCell>Upload Date</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Pages</TableCell>
                {showCoversheets && (
                  <>
                    <TableCell>Coversheet Date</TableCell>
                    <TableCell>Approved Date</TableCell>
                    <TableCell>Approved By</TableCell>
                  </>
                )}
                {visibleDocumentProperties.length > 0 ? <TableCell>Properties</TableCell> : <></>}
                {hasFirmFeatureSet && <TableCell>Firm</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <>
                  {[0, 1, 2].map((i) => (
                    <SkeletonRow key={i} columnNum={numColumns} />
                  ))}
                </>
              ) : (
                <>
                  {documents.map((intake) => (
                    <LensDocumentIntakeRow
                      key={intake.id}
                      intake={intake}
                      onSelect={(intake) => {
                        if (selectedIntakes.find((i) => i.id === intake.id)) {
                          setSelectedIntakes(selectedIntakes.filter((i) => i.id !== intake.id));
                        } else {
                          setSelectedIntakes([...selectedIntakes, intake]);
                        }
                      }}
                      actionItems={actionItems(intake)}
                      showCoversheets={showCoversheets}
                      selected={!!selectedIntakes.find((i) => i.id === intake.id)}
                      documentProperties={visibleDocumentProperties}
                    />
                  ))}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TablePagination
                sx={{
                  ".MuiTablePagination-spacer": {
                    flex: 0,
                  },
                }}
                count={intakesPagination.total}
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page - 1}
                onPageChange={(_evt, page) =>
                  updateParams([
                    { key: "page", value: `${page + 1}` },
                    { key: "rowsPerPage", value: `${pagination.rowsPerPage}` },
                  ])
                }
                onRowsPerPageChange={(evt) =>
                  updateParams([
                    { key: "rowsPerPage", value: evt.target.value },
                    { key: "page", value: `1` },
                  ])
                }
              />
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {bulkDeleteIntakeOpen && (
        <BulkDeleteIntakeDialog
          open={bulkDeleteIntakeOpen}
          close={() => {
            setBulkDeleteIntakeOpen(false);
            setSelectedIntakes([]);
            incrementCounter();
          }}
          intakes={selectedIntakes}
        />
      )}

      {selectedIntake && (
        <DeleteIntakeDialog open={deleteIntakeOpen} close={closeDeleteIntake} intake={selectedIntake} />
      )}
      {selectedIntake && (
        <ReprocessIntakeDialog open={reprocessIntakeOpen} close={closeReprocessIntake} intakeId={selectedIntake.id} />
      )}
      {selectedIntake && (
        <RenameIntakeDialog
          open={renameIntakeOpen}
          close={() => {
            setRenameIntakeOpen(false);
            setSelectedIntake(undefined);
            incrementCounter();
          }}
          intake={selectedIntake}
        />
      )}
      {selectedIntake && (
        <EditDocumentPropertiesDialog
          open={editDocumentProperties}
          close={closeEditDocumentProperties}
          intake={selectedIntake}
          documentProperties={visibleDocumentProperties}
        />
      )}
    </>
  );
};

export default LensDocuments;
